import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Button,
  Checkbox,
  Divider,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import {
  CardErrorType,
  CardErrorsItem,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { useCardEditorPanel } from "../hooks/useCardEditorPanel.tsx";
import {
  cardErrorTranslationKeys,
  cardErrorTypes,
  useCardErrorColumns,
} from "../hooks/useCardErrorColumns.tsx";
import { useCardErrors } from "../hooks/useCardErrors.ts";
import { useReloadCards } from "../hooks/useReloadCards.ts";
import { useTranslator } from "../i18n/translator.ts";
import { CardsTable } from "./CardsTable.tsx";
import { CardsTableToolbar } from "./CardsTableToolbar.tsx";
import { ErrorDisplay } from "./ErrorDisplay.tsx";
import { ProjectsWithoutShepherdAlert } from "./ProjectsWithoutShepherdAlert.tsx";

export const CardErrorsPage = () => {
  const translator = useTranslator();
  const { data, error, isFetching, isPending, refetch } = useCardErrors();
  const { handleDetailClick, selectedCardId } = useCardEditorPanel();

  const onReload = useReloadCards(refetch);
  const [activeErrorTypes, setActiveErrorTypes] = useState<CardErrorType[]>([]);
  const [draftErrorTypes, setDraftErrorTypes] = useState<CardErrorType[]>([]);
  const [filterAnchorElement, setFilterAnchorElement] =
    useState<HTMLElement | null>(null);

  const cards = data?.cards;
  const errorTypeCounts = useMemo(() => {
    const counts = Object.fromEntries(
      cardErrorTypes.map((errorType) => [errorType, 0]),
    ) as Record<CardErrorType, number>;

    cards?.forEach((card) => {
      card.errors.forEach((errorType) => {
        counts[errorType] += 1;
      });
    });

    return counts;
  }, [cards]);
  const filteredCards = useMemo(() => {
    if (activeErrorTypes.length === 0) {
      return cards ?? [];
    }
    const activeErrors = new Set(activeErrorTypes);
    return (
      cards?.filter((card) =>
        card.errors.some((errorType) => activeErrors.has(errorType)),
      ) ?? []
    );
  }, [activeErrorTypes, cards]);
  const columns = useCardErrorColumns(handleDetailClick);
  const isFilterMenuOpen = filterAnchorElement !== null;

  const openFilterMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDraftErrorTypes(activeErrorTypes);
    setFilterAnchorElement(event.currentTarget);
  };

  const closeFilterMenu = () => setFilterAnchorElement(null);

  const toggleDraftErrorType = (errorType: CardErrorType) => {
    setDraftErrorTypes((currentErrorTypes) =>
      currentErrorTypes.includes(errorType)
        ? currentErrorTypes.filter(
            (currentErrorType) => currentErrorType !== errorType,
          )
        : [...currentErrorTypes, errorType],
    );
  };

  const applyDraftFilters = () => {
    setActiveErrorTypes(draftErrorTypes);
    closeFilterMenu();
  };

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable<CardErrorsItem>({
    data: filteredCards,
    getRowId: (row) => row.id,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  return (
    <Stack padding={2} spacing={2} height="100%">
      <Typography variant="h4">{translator("cardErrorsTitle")}</Typography>
      <CardsTableToolbar
        filterControls={
          !error && (
            <Button
              id="card-errors-filter-button"
              aria-controls={
                isFilterMenuOpen ? "card-errors-filter-menu" : undefined
              }
              aria-expanded={isFilterMenuOpen ? "true" : undefined}
              aria-haspopup="menu"
              onClick={openFilterMenu}
              size="small"
              startIcon={<FilterListIcon />}
              endIcon={<ExpandMoreIcon />}
              variant="outlined"
            >
              {`${translator("cardErrorsFilterButton")} · ${
                activeErrorTypes.length === 0
                  ? translator("cardErrorsFilterAll")
                  : translator("cardErrorsSelected", {
                      count: activeErrorTypes.length,
                    })
              }`}
            </Button>
          )
        }
        onReload={onReload}
        isReloading={isFetching}
        resultInfo={
          !error && (
            <Typography color="text.secondary" variant="body2">
              {translator("cardErrorsCount", {
                shown: filteredCards.length,
                total: cards?.length ?? 0,
              })}
            </Typography>
          )
        }
      />
      {error ? (
        <ErrorDisplay error={error} />
      ) : (
        <>
          <Menu
            id="card-errors-filter-menu"
            anchorEl={filterAnchorElement}
            open={isFilterMenuOpen}
            onClose={closeFilterMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: {
                  maxWidth: "calc(100vw - 32px)",
                  width: { xs: "calc(100vw - 32px)", sm: 420 },
                },
              },
            }}
            MenuListProps={{ "aria-labelledby": "card-errors-filter-button" }}
          >
            <ListSubheader component="div" disableSticky>
              {translator("cardErrorsFilterMenuTitle")}
            </ListSubheader>
            {cardErrorTypes.map((errorType) => {
              const isSelected = draftErrorTypes.includes(errorType);
              const label = translator(cardErrorTranslationKeys[errorType]);

              return (
                <MenuItem
                  key={errorType}
                  aria-checked={isSelected}
                  aria-label={`${label} (${errorTypeCounts[errorType]})`}
                  onClick={() => toggleDraftErrorType(errorType)}
                  role="menuitemcheckbox"
                >
                  <Checkbox
                    checked={isSelected}
                    disableRipple
                    inputProps={{ "aria-hidden": true }}
                    tabIndex={-1}
                  />
                  <ListItemText primary={label} />
                  <Typography color="text.secondary" variant="body2">
                    {errorTypeCounts[errorType]}
                  </Typography>
                </MenuItem>
              );
            })}
            <Divider />
            <Stack direction="row" gap={1} justifyContent="flex-end" p={1}>
              <Button onClick={() => setDraftErrorTypes([])} size="small">
                {translator("cardErrorsResetFilters")}
              </Button>
              <Button
                onClick={applyDraftFilters}
                size="small"
                variant="contained"
              >
                {translator("cardErrorsApplyFilters")}
              </Button>
            </Stack>
          </Menu>
          <ProjectsWithoutShepherdAlert
            projects={data?.projectsWithoutShepherd ?? []}
          />
          {isPending ? null : filteredCards.length > 0 ? (
            <CardsTable
              table={table}
              isRowSelected={(id) => selectedCardId === id}
            />
          ) : (
            <Typography color="text.secondary">
              {translator(
                cards?.length
                  ? "cardErrorsNoMatchingCards"
                  : "cardErrorsNoErrors",
              )}
            </Typography>
          )}
        </>
      )}
    </Stack>
  );
};
